/**
 * Safe Context Hooks - Layout-safe versions of context hooks
 * 
 * These hooks are designed to be called from layout components that render
 * BEFORE route-specific context providers are available. They provide proper
 * loading states and initialization guards instead of throwing errors.
 * 
 * ENHANCED FEATURES:
 * - Context readiness detection with initialization delays
 * - Proper loading states that distinguish between initializing and unavailable
 * - Safe fallback objects with warning functions instead of crashes
 * - Comprehensive availability and readiness checks
 * - Initialization guard utilities for better UX
 * 
 * USAGE EXAMPLE:
 * ```tsx
 * import { useSafeContextData, useContextInitializationGuard } from './useSafeContextHooks';
 * 
 * const MyComponent = () => {
 *   const { 
 *     entities, 
 *     contextReady, 
 *     isInitializing,
 *     overallLoading 
 *   } = useSafeContextData();
 *   
 *   const { canRender, shouldShowLoading } = useContextInitializationGuard();
 *   
 *   if (shouldShowLoading) {
 *     return <LoadingSpinner />;
 *   }
 *   
 *   if (!canRender) {
 *     return <ErrorFallback />;
 *   }
 *   
 *   return <MyActualComponent data={entities} />;
 * };
 * ```
 * 
 * Use these in components like SmartSecondarySidebar that render at the
 * layout level but need to access route-specific contexts.
 */

import { useState, useEffect, useMemo } from 'react';
import { useVRPData } from '../contexts/VRPDataContext';
import { useProject } from '../contexts/ProjectContext';
import { useScenario } from '../contexts/ScenarioContext';

/**
 * Context Readiness Hook - Checks if contexts are properly initialized
 * Provides a small delay to allow contexts to initialize before declaring ready
 */
export const useContextReady = () => {
  const [isReady, setIsReady] = useState(false);
  const [initializationDelay, setInitializationDelay] = useState(true);

  useEffect(() => {
    // Small delay to allow contexts to initialize
    const timer = setTimeout(() => {
      setInitializationDelay(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!initializationDelay) {
      setIsReady(true);
    }
  }, [initializationDelay]);

  return {
    isReady,
    isInitializing: initializationDelay
  };
};

/**
 * Safe VRP Data Hook - Layout Safe with Enhanced Loading States
 * Always available since VRPDataProvider is at app level
 */
export const useVRPDataSafe = () => {
  const { isReady, isInitializing } = useContextReady();
  
  try {
    const context = useVRPData();
    
    // Check if context is properly initialized
    const isContextReady = context && !context.loading?.initializing;
    
    return {
      ...context,
      loading: {
        ...context.loading,
        initializing: isInitializing || context.loading?.initializing || false,
        contextReady: isReady && isContextReady
      }
    };
  } catch (error) {
    console.warn('[useSafeContextHooks] VRPData context not available:', error);
    return {
      entities: {
        vehicles: [],
        locations: [],
        jobs: [],
        products: [],
        skills: [],
        scenarios: []
      },
      loading: { 
        initializing: true,
        contextReady: false
      },
      errors: {},
      cache: {
        lastUpdated: {},
        isStale: {},
        retryCount: {}
      },
      // Safe fallback functions
      create: async () => { throw new Error('Context not ready'); },
      update: async () => { throw new Error('Context not ready'); },
      delete: async () => { throw new Error('Context not ready'); },
      bulkCreate: async () => { throw new Error('Context not ready'); },
      bulkUpdate: async () => { throw new Error('Context not ready'); },
      bulkDelete: async () => { throw new Error('Context not ready'); },
      refresh: async () => { console.warn('Context not ready for refresh'); },
      clearCache: () => { console.warn('Context not ready for cache clear'); }
    };
  }
};

/**
 * Safe Project Hook - Layout Safe with Enhanced Loading States
 * Returns proper loading state when ProjectProvider is not available (layout level)
 */
export const useProjectSafe = () => {
  const { isReady, isInitializing } = useContextReady();
  
  try {
    const context = useProject();
    
    if (!context) {
      return {
        currentProject: null,
        setCurrentProject: () => { console.warn('Project context not ready'); },
        projects: [],
        isLoading: true,
        error: null,
        restoreProjectFromUrl: () => { console.warn('Project context not ready'); },
        navigateToProject: () => { console.warn('Project context not ready'); },
        getProjectUrl: () => '',
        contextReady: false,
        isInitializing: true
      };
    }
    
    return {
      ...context,
      contextReady: isReady && !context.isLoading,
      isInitializing: isInitializing || context.isLoading,
      isLoading: isInitializing || context.isLoading
    };
  } catch (error) {
    console.debug('[useSafeContextHooks] Project context not available (expected at layout level)');
    return {
      currentProject: null,
      setCurrentProject: () => { console.warn('Project context not available'); },
      projects: [],
      isLoading: true,
      error: null,
      restoreProjectFromUrl: () => { console.warn('Project context not available'); },
      navigateToProject: () => { console.warn('Project context not available'); },
      getProjectUrl: () => '',
      contextReady: false,
      isInitializing: true
    };
  }
};

/**
 * Safe Scenario Hook - Layout Safe with Enhanced Loading States
 * Returns proper loading state when ScenarioProvider is not available (layout level)
 */
export const useScenarioSafe = () => {
  const { isReady, isInitializing } = useContextReady();
  
  try {
    const context = useScenario();
    
    if (!context) {
      return {
        currentProject: null,
        currentScenario: null,
        scenarios: [],
        isLoading: true,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        error: null,
        validationErrors: null,
        setScenario: () => { console.warn('Scenario context not ready'); },
        createScenario: async () => { throw new Error('Scenario context not ready'); },
        updateScenario: async () => { throw new Error('Scenario context not ready'); },
        deleteScenario: async () => { throw new Error('Scenario context not ready'); },
        duplicateScenario: async () => { throw new Error('Scenario context not ready'); },
        refreshScenarios: async () => { console.warn('Scenario context not ready'); },
        getScenarioDetails: async () => null,
        clearError: () => { console.warn('Scenario context not ready'); },
        isScenarioSelected: () => false,
        contextReady: false,
        isInitializing: true
      };
    }
    
    return {
      ...context,
      contextReady: isReady && !context.isLoading,
      isInitializing: isInitializing || context.isLoading,
      isLoading: isInitializing || context.isLoading
    };
  } catch (error) {
    console.debug('[useSafeContextHooks] Scenario context not available (expected at layout level)');
    return {
      currentProject: null,
      currentScenario: null,
      scenarios: [],
      isLoading: true,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,
      validationErrors: null,
      setScenario: () => { console.warn('Scenario context not available'); },
      createScenario: async () => { throw new Error('Scenario context not available'); },
      updateScenario: async () => { throw new Error('Scenario context not available'); },
      deleteScenario: async () => { throw new Error('Scenario context not available'); },
      duplicateScenario: async () => { throw new Error('Scenario context not available'); },
      refreshScenarios: async () => { console.warn('Scenario context not available'); },
      getScenarioDetails: async () => null,
      clearError: () => { console.warn('Scenario context not available'); },
      isScenarioSelected: () => false,
      contextReady: false,
      isInitializing: true
    };
  }
};

/**
 * Context Availability Check with Enhanced Readiness Detection
 * Returns which contexts are currently available and ready for use
 */
export const useContextAvailability = () => {
  const vrpData = useVRPDataSafe();
  const projectData = useProjectSafe();
  const scenarioData = useScenarioSafe();

  return useMemo(() => {
    const hasVRPContext = vrpData !== null;
    const hasProjectContext = projectData !== null;
    const hasScenarioContext = scenarioData !== null;
    
    // Check if contexts are ready (not just available)
    const vrpReady = hasVRPContext && vrpData.loading?.contextReady;
    const projectReady = hasProjectContext && projectData.contextReady;
    const scenarioReady = hasScenarioContext && scenarioData.contextReady;
    
    // Check if contexts are still initializing
    const vrpInitializing = hasVRPContext && vrpData.loading?.initializing;
    const projectInitializing = hasProjectContext && projectData.isInitializing;
    const scenarioInitializing = hasScenarioContext && scenarioData.isInitializing;

    return {
      // Availability
      hasVRPContext,
      hasProjectContext,
      hasScenarioContext,
      hasAllContexts: hasVRPContext && hasProjectContext && hasScenarioContext,
      contextCount: [vrpData, projectData, scenarioData].filter(Boolean).length,
      
      // Readiness
      vrpReady,
      projectReady,
      scenarioReady,
      allContextsReady: vrpReady && projectReady && scenarioReady,
      readyContextCount: [vrpReady, projectReady, scenarioReady].filter(Boolean).length,
      
      // Initialization status
      vrpInitializing,
      projectInitializing,
      scenarioInitializing,
      anyContextInitializing: vrpInitializing || projectInitializing || scenarioInitializing,
      
      // Overall status
      systemReady: vrpReady && !vrpInitializing,
      projectSystemReady: hasProjectContext ? (projectReady && !projectInitializing) : true,
      scenarioSystemReady: hasScenarioContext ? (scenarioReady && !scenarioInitializing) : true,
      fullyReady: vrpReady && 
                  (hasProjectContext ? projectReady : true) && 
                  (hasScenarioContext ? scenarioReady : true) &&
                  !vrpInitializing && 
                  !projectInitializing && 
                  !scenarioInitializing
    };
  }, [vrpData, projectData, scenarioData]);
};

/**
 * Layout-Safe Data Extraction with Enhanced Loading States
 * Extracts data from contexts with safe fallbacks and readiness detection
 */
export const useSafeContextData = () => {
  const vrpData = useVRPDataSafe();
  const projectData = useProjectSafe();
  const scenarioData = useScenarioSafe();
  const availability = useContextAvailability();

  return useMemo(() => ({
    // VRP Data (always available)
    entities: vrpData.entities,
    loading: vrpData.loading,
    errors: vrpData.errors,
    cache: vrpData.cache,

    // Project Data (may be null)
    currentProject: projectData?.currentProject || null,
    projects: projectData?.projects || [],
    projectsLoading: projectData?.isLoading || false,
    projectError: projectData?.error || null,

    // Scenario Data (may be null)  
    currentScenario: scenarioData?.currentScenario || null,
    scenarios: scenarioData?.scenarios || [],
    scenariosLoading: scenarioData?.isLoading || false,
    scenarioError: scenarioData?.error || null,

    // Context availability and readiness
    hasProjectContext: projectData !== null,
    hasScenarioContext: scenarioData !== null,
    contextReady: availability.fullyReady,
    systemReady: availability.systemReady,
    isInitializing: availability.anyContextInitializing,

    // Individual context readiness
    vrpReady: availability.vrpReady,
    projectReady: availability.projectReady,
    scenarioReady: availability.scenarioReady,

    // Loading state summary
    overallLoading: availability.anyContextInitializing || 
                   vrpData.loading?.initializing || 
                   projectData?.isLoading || 
                   scenarioData?.isLoading,

    // Error state summary
    hasErrors: !!(vrpData.errors && Object.keys(vrpData.errors).length > 0) ||
               !!projectData?.error ||
               !!scenarioData?.error
  }), [vrpData, projectData, scenarioData, availability]);
};

/**
 * Context Initialization Guard Hook
 * Provides utilities for components to handle context initialization gracefully
 */
export const useContextInitializationGuard = () => {
  const availability = useContextAvailability();
  const [hasWarned, setHasWarned] = useState(false);

  // Warn once if contexts are not ready after a reasonable time
  useEffect(() => {
    if (!availability.fullyReady && !hasWarned) {
      const timer = setTimeout(() => {
        if (!availability.fullyReady) {
          console.warn('[Context Guard] Contexts not ready after initialization period:', {
            systemReady: availability.systemReady,
            projectReady: availability.projectReady,
            scenarioReady: availability.scenarioReady,
            initializing: availability.anyContextInitializing
          });
          setHasWarned(true);
        }
      }, 2000); // 2 second warning threshold

      return () => clearTimeout(timer);
    }
  }, [availability.fullyReady, hasWarned, availability]);

  return {
    isReady: availability.fullyReady,
    isInitializing: availability.anyContextInitializing,
    canRender: availability.systemReady, // At minimum, VRP context should be ready
    shouldShowLoading: availability.anyContextInitializing,
    initializationStatus: {
      vrp: availability.vrpReady,
      project: availability.projectReady,
      scenario: availability.scenarioReady
    }
  };
};